import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';
import { countUnreadThreadsForIdentities } from '../messages/storage';
import { countUnreadGroupChatsForIdentities } from '../groupChats/storage';
import { useCallHistoryStore } from '../store/callHistoryStore';
import { useAuthStore } from '../store/authStore';
import { useCallStore } from '../store/callStore';
import { useMkApprovalWatcher } from '../hooks/useMkApprovalWatcher';
import { useMkConflictWatcher } from '../hooks/useMkConflictWatcher';
import { isWebViewMode, resumeNativeCall, setNativeToolbarVisible } from '../utils/webviewBridge';
import { useLayoutChrome } from './layoutChrome';

interface MainLayoutProps {
  children: ReactNode;
  hideNav?: boolean;
  headerMode?: 'default' | 'brand' | 'title' | 'none';
  headerTitle?: string;
  showQrActions?: boolean;
  headerActions?: ReactNode;
  forceHeaderInWebView?: boolean;
}

function ActiveCallBanner() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const activeCall = useCallStore((s) => s.activeCall);
  const isCallMinimized = useCallStore((s) => s.isCallMinimized);
  const restoreCall = useCallStore((s) => s.restoreCall);

  if (!activeCall || !isCallMinimized) return null;

  return (
    <div className="sticky top-0 z-20 bg-primary-50 border-b border-primary-200">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <p className="text-sm text-primary-900 truncate">
          {t('call.banner.activeWithName', { name: activeCall.remoteDisplayName || t('common.unknown') })}
        </p>
        <button
          type="button"
          className="btn btn-primary whitespace-nowrap"
          onClick={() => {
            restoreCall();
            if (isWebViewMode && activeCall.nativeRuntimeMode === 'video-native') {
              resumeNativeCall();
              return;
            }
            navigate('/call');
          }}
        >
          {t('call.banner.return')}
        </button>
      </div>
    </div>
  );
}

export function MainLayout({
  children,
  hideNav
}: MainLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { embedded } = useLayoutChrome();
  useMkApprovalWatcher();
  useMkConflictWatcher();

  const identitiesById = useAuthStore((state) => state.identities);
  const localIdentityIds = useMemo(() => Object.keys(identitiesById), [identitiesById]);

  const [unreadThreadsCount, setUnreadThreadsCount] = useState(0);

  const unseenMissedCallersCount = useCallHistoryStore(
    (state) => {
      const unseenCallers = new Set<string>();
      for (const e of state.events) {
        const isIncomingMissed = e.direction === 'incoming'
          && (
            e.status === 'failed'
            || (e.status === 'ended' && !e.durationSeconds)
          );
        if (!isIncomingMissed) continue;
        if (e.seen !== false) continue;
        const caller = (e.remoteIdentityId || '').trim();
        if (caller) unseenCallers.add(caller);
      }
      return unseenCallers.size;
    }
  );

  useEffect(() => {
    let mounted = true;

    const compute = async () => {
      if (localIdentityIds.length === 0) {
        if (mounted) setUnreadThreadsCount(0);
        return;
      }
      const [unreadThreads, unreadGroupChats] = await Promise.all([
        countUnreadThreadsForIdentities(localIdentityIds),
        countUnreadGroupChatsForIdentities(localIdentityIds)
      ]);
      if (mounted) setUnreadThreadsCount(unreadThreads + unreadGroupChats);
    };

    void compute();
    const handler = () => void compute();
    window.addEventListener('messages:updated', handler);
    window.addEventListener('group-chats:updated', handler);
    return () => {
      mounted = false;
      window.removeEventListener('messages:updated', handler);
      window.removeEventListener('group-chats:updated', handler);
    };
  }, [localIdentityIds]);

  type TabItem = {
    path: string;
    label: string;
    shortLabel: string;
    icon: string;
    badgeCount?: number;
  };

  const tabs: TabItem[] = useMemo(
    () => [
      {
        path: '/messages',
        label: t('nav.messages'),
        shortLabel: t('nav.messagesShort'),
        icon: 'chat',
        badgeCount: unreadThreadsCount + unseenMissedCallersCount
      },
      { path: '/servers', label: t('nav.servers'), shortLabel: t('nav.serversShort'), icon: 'server' },
      {
        path: '/contacts',
        label: t('nav.contacts'),
        shortLabel: t('nav.contactsShort'),
        icon: 'contact',
      },
      { path: '/settings', label: t('nav.settings'), shortLabel: t('nav.settingsShort'), icon: 'settings' }
    ],
    [t, unreadThreadsCount, unseenMissedCallersCount]
  );

  const isPrimaryShellRoute =
    location.pathname === '/messages' ||
    location.pathname === '/servers' ||
    location.pathname === '/contacts' ||
    location.pathname === '/settings';

  const shouldShowNav = isPrimaryShellRoute && !hideNav;

  useEffect(() => {
    setNativeToolbarVisible(isPrimaryShellRoute);
  }, [isPrimaryShellRoute]);

  if (embedded) {
    return <>{children}</>;
  }

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const renderBadge = (count?: number) => {
    if (!count || count <= 0) return null;
    const text = count > 99 ? '99+' : String(count);
    return (
      <span
        className="absolute -top-1 -right-2 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-primary-600 text-white text-[0.6rem] leading-[1.1rem] font-semibold text-center"
        aria-label={text}
      >
        {text}
      </span>
    );
  };

  const renderIcon = (icon: string, badgeCount?: number, active = false) => {
    const iconClass = active
      ? 'w-[1.15rem] h-[1.15rem] sm:w-[1.35rem] sm:h-[1.35rem]'
      : 'w-[1.05rem] h-[1.05rem] sm:w-[1.25rem] sm:h-[1.25rem]';
    const strokeWidth = active ? 2.25 : 2;

    switch (icon) {
      case 'phone':
        return (
          <div className="relative">
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          {renderBadge(badgeCount)}
          </div>
        );
      case 'contact':
        return (
          <div className="relative">
            <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M15 8a3 3 0 11-6 0 3 3 0 016 0zM5 19a7 7 0 0114 0" />
            </svg>
            {renderBadge(badgeCount)}
          </div>
        );
      case 'chat':
        return (
          <div className="relative">
            <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M8 10h8M8 14h5M21 12a9 9 0 11-4-7.5L21 12z" />
            </svg>
            {renderBadge(badgeCount)}
          </div>
        );
      case 'server':
        return (
          <div className="relative">
            <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="9" cy="15" r="4.8" strokeWidth={active ? 2.45 : 2.2} />
              <circle cx="15" cy="9" r="4.8" strokeWidth={active ? 2.45 : 2.2} />
              {/* маленькая "связь" в точке касания — можно удалить, если хочешь ультра-минимализм */}
              <path strokeLinecap="round" d="M11.7 12.3 L12.3 11.7" strokeWidth={active ? 2.2 : 1.9} />
            </svg>
          </div>
        );
      case 'settings':
        return (
          <div className="relative">
            <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="6" strokeWidth={strokeWidth} />
              <circle cx="12" cy="12" r="2.5" strokeWidth={strokeWidth} />

              <path strokeLinecap="round" strokeWidth={strokeWidth} d="M12 3.5 L12 5.3" />
              <path strokeLinecap="round" strokeWidth={strokeWidth} d="M12 18.7 L12 20.5" />
              <path strokeLinecap="round" strokeWidth={strokeWidth} d="M3.5 12 L5.3 12" />
              <path strokeLinecap="round" strokeWidth={strokeWidth} d="M18.7 12 L20.5 12" />

              <path strokeLinecap="round" strokeWidth={strokeWidth} d="M6.2 6.2 L7.5 7.5" />
              <path strokeLinecap="round" strokeWidth={strokeWidth} d="M16.5 16.5 L17.8 17.8" />
              <path strokeLinecap="round" strokeWidth={strokeWidth} d="M16.5 7.5 L17.8 6.2" />
              <path strokeLinecap="round" strokeWidth={strokeWidth} d="M6.2 17.8 L7.5 16.5" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden text-[color:var(--text-strong)]">
      <ActiveCallBanner />

      <div className={`flex-1 min-h-0 overflow-hidden transition-[padding] duration-300 ${shouldShowNav ? 'pb-[3.95rem] sm:pb-[4.75rem]' : 'pb-0'}`}>
        {children}
      </div>

      <nav className={`fixed bottom-0 left-0 right-0 z-50 px-3 pb-nav-safe sm:px-6 transition-transform duration-300 ${shouldShowNav ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="mx-auto max-w-5xl rounded-[1.35rem] border border-[rgba(105,76,58,0.07)] bg-[rgba(255,252,250,0.88)] shadow-[0_11px_24px_rgba(53,38,27,0.09)] backdrop-blur-[14px]">
          <div className="flex justify-around px-1 py-1 sm:px-1.5 sm:py-1.5">
            {tabs.map((tab) => (
              <button
                key={tab.path}
                onClick={() => navigate(tab.path)}
                className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-[0.28rem] rounded-[1.1rem] px-1 py-2 transition-all sm:px-4 sm:py-2.5 ${
                  isActive(tab.path)
                    ? 'bg-[linear-gradient(180deg,rgba(233,92,63,0.14),rgba(233,92,63,0.08))] text-[color:var(--text-strong)] shadow-[inset_0_0_0_1px_rgba(232,92,63,0.18),0_1px_0_rgba(255,255,255,0.45)]'
                    : 'text-[rgba(86,69,58,0.72)] hover:bg-white/72 hover:text-[color:var(--text-strong)]'
                }`}
                aria-current={isActive(tab.path) ? 'page' : undefined}
              >
                {renderIcon(tab.icon, tab.badgeCount, isActive(tab.path))}
                <span className={`text-[10px] leading-none tracking-[-0.01em] sm:text-xs ${
                  isActive(tab.path) ? 'font-semibold' : 'font-medium'
                }`}>
                  <span className="sm:hidden">{tab.shortLabel}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </nav>
    </div>
  );
}
